import { useState } from 'react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Mail, ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface EmailHistoryRow {
  created_at: string;
  status: string;
  template: string;
  subject: string;
  recipient_count: number;
  last_sent_at: string | null;
}

interface EventEmailHistorySectionProps {
  eventId: string;
}

function useEventEmailHistory(eventId: string) {
  return useQuery({
    queryKey: ['event-email-history', eventId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_event_email_history', {
        p_event_id: eventId,
        p_limit: 20,
      });
      if (error) throw error;
      return (data ?? []) as unknown as EmailHistoryRow[];
    },
    enabled: !!eventId,
  });
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'sent':
      return <Badge variant="secondary" className="text-[11px]">Skickat</Badge>;
    case 'pending':
      return <Badge variant="outline" className="text-[11px]">Väntar</Badge>;
    case 'failed':
      return <Badge variant="destructive" className="text-[11px] opacity-80">Misslyckades</Badge>;
    default:
      return <Badge variant="outline" className="text-[11px]">{status}</Badge>;
  }
}

const templateLabels: Record<string, string> = {
  booking_confirmation: 'Bokningsbekräftelse',
  event_update: 'Uppdatering',
  booking_cancellation: 'Avbokning',
};

export function EventEmailHistorySection({ eventId }: EventEmailHistorySectionProps) {
  const { data: rows, isLoading } = useEventEmailHistory(eventId);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<EmailHistoryRow | null>(null);

  const totalCount = rows?.reduce((sum, r) => sum + r.recipient_count, 0) ?? 0;

  if (isLoading || !rows || rows.length === 0) {
    return (
      <div className="mt-6">
        <p className="text-xs text-muted-foreground/60">Inga meddelanden skickade ännu.</p>
      </div>
    );
  }

  return (
    <>
      <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mt-6">
        <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full py-2">
          <Mail className="h-4 w-4" />
          <span className="font-medium">Meddelanden skickade</span>
          <span className="text-xs text-muted-foreground/70">({totalCount})</span>
          <ChevronDown className={`h-3.5 w-3.5 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </CollapsibleTrigger>

        <CollapsibleContent className="pt-1">
          <p className="text-xs text-muted-foreground/60 mb-3">
            Visar senaste utskicken kopplade till denna aktivitet.
          </p>
          <div className="space-y-1">
            {rows.map((row, i) => (
              <button
                key={`${row.created_at}-${row.template}-${i}`}
                onClick={() => setSelectedRow(row)}
                className="w-full flex items-center gap-3 text-left text-sm py-2 px-3 rounded-md hover:bg-muted/50 transition-colors"
              >
                <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
                  {format(new Date(row.created_at), 'd MMM HH:mm', { locale: sv })}
                </span>
                <span className="truncate flex-1 text-foreground/90">
                  {row.subject}
                </span>
                {row.recipient_count > 1 && (
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {row.recipient_count} mottagare
                  </span>
                )}
                <StatusBadge status={row.status} />
              </button>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <Dialog open={!!selectedRow} onOpenChange={(open) => !open && setSelectedRow(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">E-postdetaljer</DialogTitle>
          </DialogHeader>
          {selectedRow && (
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-muted-foreground text-xs uppercase tracking-wide">Ämne</span>
                <p className="font-medium">{selectedRow.subject}</p>
              </div>
              <div>
                <span className="text-muted-foreground text-xs uppercase tracking-wide">Mottagare</span>
                <p className="text-foreground/80">
                  {selectedRow.recipient_count} {selectedRow.recipient_count === 1 ? 'mottagare' : 'mottagare'}
                </p>
              </div>
              <div className="flex gap-6">
                <div>
                  <span className="text-muted-foreground text-xs uppercase tracking-wide">Status</span>
                  <div className="mt-0.5"><StatusBadge status={selectedRow.status} /></div>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs uppercase tracking-wide">Typ</span>
                  <p className="mt-0.5">{templateLabels[selectedRow.template] ?? selectedRow.template}</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div>
                  <span className="text-muted-foreground text-xs uppercase tracking-wide">Skapad</span>
                  <p className="mt-0.5">{format(new Date(selectedRow.created_at), "d MMMM yyyy, HH:mm", { locale: sv })}</p>
                </div>
                {selectedRow.last_sent_at && (
                  <div>
                    <span className="text-muted-foreground text-xs uppercase tracking-wide">Skickad</span>
                    <p className="mt-0.5">{format(new Date(selectedRow.last_sent_at), "d MMMM yyyy, HH:mm", { locale: sv })}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
